/**
 * inferCountryFromAddress — best-effort address → ISO-2 country code.
 *
 * Only fires when confidence is HIGH. Vague matches return null so the
 * scan review just shows "Select country" rather than a guess.
 *
 * Signals in order of decreasing confidence:
 *   1. UK postcode regex (SW1A 1AA, EC2R 8AH, ...)
 *   2. UAE keywords (Dubai / Abu Dhabi / Sharjah / UAE / United Arab Emirates)
 *   3. US state codes with ZIP (", NY 10001") + "USA"/"United States"
 *   4. "United Kingdom"/"UK"/"England"/"Scotland"/"Wales" (only when address
 *      is comma-separated to avoid false positives on marketing copy)
 *   5. FR/DE/ES/IT/AU/CA keyword whitelist
 *
 * Never uses the browser locale or the HQ operator's location.
 */
import { parsePhoneNumberFromString } from "libphonenumber-js";

const UK_POSTCODE = /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i;
const US_ZIP_STATE = /\b(A[LKZR]|C[AOT]|D[EC]|FL|GA|HI|I[LNAD]|K[SY]|LA|M[EDATNSMO]|N[EVHJMYCD]|O[HKR]|P[AR]|RI|S[CD]|T[NX]|UT|V[TA]|W[VAIY])\s+\d{5}(-\d{4})?\b/i;
const UAE_HINT = /\b(dubai|abu dhabi|sharjah|ajman|ras al[- ]?khaimah|umm al[- ]?quwain|fujairah|u\.?a\.?e\.?|united arab emirates)\b/i;
const UK_TEXT_HINT = /\b(london|manchester|birmingham|glasgow|edinburgh|liverpool|united kingdom|england|scotland|wales)\b/i;
const US_TEXT_HINT = /\b(new york|los angeles|san francisco|chicago|united states|usa)\b/i;
const KEYWORD_MAP = [
  { re: /\bparis\b|\bfrance\b/i, code: "FR" },
  { re: /\bberlin\b|\bmunich\b|\bgermany\b/i, code: "DE" },
  { re: /\bmadrid\b|\bbarcelona\b|\bspain\b/i, code: "ES" },
  { re: /\bmilan\b|\brome\b|\bitaly\b/i, code: "IT" },
  { re: /\bsydney\b|\bmelbourne\b|\baustralia\b/i, code: "AU" },
  { re: /\btoronto\b|\bvancouver\b|\bcanada\b/i, code: "CA" },
  { re: /\bmumbai\b|\bbengaluru\b|\bdelhi\b|\bindia\b/i, code: "IN" },
];

export function inferCountryFromAddress(address) {
  const s = (address || "").trim();
  if (!s) return null;
  if (UK_POSTCODE.test(s) || UK_TEXT_HINT.test(s)) return "GB";
  if (UAE_HINT.test(s)) return "AE";
  if (US_ZIP_STATE.test(s) || US_TEXT_HINT.test(s)) return "US";
  for (const { re, code } of KEYWORD_MAP) {
    if (re.test(s)) return code;
  }
  return null;
}

/**
 * inferCountryFromPhone — parse a discovered E.164/international phone
 * and return its ISO-2 country code. Returns null for local/national
 * format numbers (e.g. "020 7123 4567" without a leading +).
 */
export function inferCountryFromPhone(phone) {
  const s = (phone || "").trim();
  if (!s.startsWith("+")) return null;
  try {
    const p = parsePhoneNumberFromString(s);
    return p?.country || null;
  } catch {
    return null;
  }
}
